import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useAddSopTask,
  useEditSopTask,
  useSkillOptions,
} from "../../../../hooks";
import { useSkills } from "@features/wx/settings/hooks";
import { Loader2, Clock, AlertTriangle } from "lucide-react";

interface Task {
  name: string;
  time: string;
  downtime: string;
  skillId: number[];
  isPhotoReq: string | null;
  skillName: string[];
}

interface TaskFormData {
  name: string;
  time: string;
  downtime: string;
  skillIds: number[]; // Changed from skills string to skillIds array
  photoRequired: boolean;
}

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
}

interface EditTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
  task: Task | null;
  taskIndex: number;
}

// Enhanced Add Task Modal Component
export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  sopId,
}) => {
  const { toast } = useToast();
  const addTaskMutation = useAddSopTask();
  const { options: skillOptions, getSkillIdByName } = useSkillOptions();
  const { data: skills = [], isLoading: loadingSkills } = useSkills();

  // Transform skills for form options (matching Tasks Tab pattern)
  const skillFormOptions = useMemo(
    () =>
      skills.map((skill) => ({
        value: skill.id,
        label: skill.name,
      })),
    [skills]
  );

  // Enhanced form fields configuration (matching Tasks Tab complexity)
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Task Name",
        placeholder: "Enter task name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the task",
      },
      {
        name: "time",
        type: "number",
        label: "Turnaround Time (minutes)",
        placeholder: "30",
        required: true,
        min: 1,
        max: 9999,
        suffix: <Clock className="h-4 w-4 text-muted-foreground" />,
        helperText: "Expected time to complete this task",
      },
      {
        name: "downtime",
        type: "number",
        label: "Downtime (minutes)",
        placeholder: "5",
        required: true,
        min: 0,
        max: 9999,
        suffix: <AlertTriangle className="h-4 w-4 text-muted-foreground" />,
        helperText: "Expected downtime caused by this task",
      },
      {
        name: "skillIds",
        type: "multi-select",
        label: "Required Skills",
        placeholder: "Select required skills...",
        required: false,
        disabled: loadingSkills,
        options: skillFormOptions,
        searchable: true,
        clearable: true,
        maxDisplayItems: 3,
        helperText: "Choose skills required to perform this task",
      },
      {
        name: "photoRequired",
        type: "switch",
        label: "Photo Required",
        helperText: "Enable if this task requires photo documentation",
        defaultValue: false,
      },
      {
        name: "taskMandatory",
        type: "switch",
        label: "Task Mandatory",
        helperText: "Enable if this task is mandatory",
        defaultValue: false,
      },
    ],
    [skillFormOptions, loadingSkills]
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      time: "0",
      downtime: "0",
      skillIds: [],
      photoRequired: false,
      taskMandatory: false,
    },
  });

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
  }, []);

  // Handle form submission
  const handleSubmit = async (data: TaskFormData) => {
    try {
      await addTaskMutation.mutateAsync({
        sopId,
        taskData: {
          name: data.name,
          time: data.time,
          downtime: data.downtime,
          skill_id: data.skillIds || [],
          is_photo_req: data.photoRequired ? "Yes" : "No",
        },
      });

      toast({
        title: "Success",
        description: "Task added successfully",
      });

      // Reset form and close modal
      FormService.resetForm(form, formFields);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add task",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      FormService.resetForm(form, formFields);
    }
  }, [isOpen, form, formFields]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Task</DialogTitle>
          <DialogDescription>
            Add a new task to this SOP template
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={addTaskMutation.isPending}
            gridColumns={2} // Changed to 2 columns like Tasks Tab
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label: "block text-sm font-medium text-gray-900 mb-1",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={addTaskMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={addTaskMutation.isPending || loadingSkills}
                >
                  {addTaskMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Task"
                  )}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Edit Task Modal Component
export const EditTaskModal: React.FC<EditTaskModalProps> = ({
  isOpen,
  onClose,
  sopId,
  task,
  taskIndex,
}) => {
  const { toast } = useToast();
  const editTaskMutation = useEditSopTask();
  const { getSkillIdByName } = useSkillOptions();
  const { data: skills = [], isLoading: loadingSkills } = useSkills();

  // Transform skills for form options
  const skillFormOptions = useMemo(
    () =>
      skills.map((skill) => ({
        value: skill.id,
        label: skill.name,
      })),
    [skills]
  );

  // Form fields configuration (same as Add modal)
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Task Name",
        placeholder: "Enter task name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the task",
      },
      {
        name: "time",
        type: "number",
        label: "Turnaround Time (minutes)",
        placeholder: "30",
        required: true,
        min: 1,
        max: 9999,
        suffix: <Clock className="h-4 w-4 text-muted-foreground" />,
        helperText: "Expected time to complete this task",
      },
      {
        name: "downtime",
        type: "number",
        label: "Downtime (minutes)",
        placeholder: "5",
        required: true,
        min: 0,
        max: 9999,
        suffix: <AlertTriangle className="h-4 w-4 text-muted-foreground" />,
        helperText: "Expected downtime caused by this task",
      },
      {
        name: "skillIds",
        type: "multi-select",
        label: "Required Skills",
        placeholder: "Select required skills...",
        required: false,
        disabled: loadingSkills,
        options: skillFormOptions,
        searchable: true,
        clearable: true,
        maxDisplayItems: 3,
        helperText: "Choose skills required to perform this task",
      },
      {
        name: "photoRequired",
        type: "switch",
        label: "Photo Required",
        helperText: "Enable if this task requires photo documentation",
        defaultValue: false,
      },
      {
        name: "taskMandatory",
        type: "switch",
        label: "Task Mandatory",
        helperText: "Enable if this task is mandatory",
        defaultValue: false,
      },
    ],
    [skillFormOptions, loadingSkills]
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      time: "0",
      downtime: "0",
      skillIds: [],
      photoRequired: false,
      taskMandatory: false,
    },
  });

  // Populate form when task changes
  useEffect(() => {
    if (task && isOpen) {
      const populateData = {
        name: task.name || "",
        time: task.time || "0",
        downtime: task.downtime || "0",
        skillIds: task.skillId || [], // Use the array directly instead of converting from names
        photoRequired: task.isPhotoReq === "Yes",
      };

      FormService.populateForm(form, populateData, formFields);
    }
  }, [task, isOpen, form, formFields]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
  }, []);

  // Handle form submission
  const handleSubmit = async (data: TaskFormData) => {
    if (!task) return;

    try {
      await editTaskMutation.mutateAsync({
        sopId,
        existingValue: task.name, // Use original task name as existing value
        taskData: {
          name: data.name,
          time: data.time,
          downtime: data.downtime,
          skill_id: data.skillIds || [],
          is_photo_req: data.photoRequired ? "Yes" : "No",
        },
      });

      toast({
        title: "Success",
        description: "Task updated successfully",
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>Update the task details</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={editTaskMutation.isPending}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label: "block text-sm font-medium text-gray-900 mb-1",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={editTaskMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={editTaskMutation.isPending || loadingSkills}
                >
                  {editTaskMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Task"
                  )}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
